import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

/**
 * Requests location permission and returns current position coordinates.
 * Works on native (Android/iOS) and web (browser) platforms.
 * Throws error if permission denied or unsupported.
 *
 * @returns {Promise<{latitude: number, longitude: number, accuracy: number}>}
 */
export async function requestLocationPermission() {
  if (Capacitor.isNativePlatform()) {
    const permission = await Geolocation.requestPermissions();

    if (permission.location !== 'granted' && permission.coarseLocation !== 'granted') {
      throw new Error('Location permission denied');
    }

    try {
      // First try with high accuracy (precise/GPS)
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 60000,
      });
      return position.coords;
    } catch (err) {
      console.warn('High accuracy failed, falling back to approximate', err);
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 120000,
      });
      return position.coords;
    }
  } else {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by your browser');
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos.coords),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 }
      );
    });
  }
}

/**
 * Gets current position coordinates.
 * Assumes permission was already granted.
 *
 * @returns {Promise<{latitude: number, longitude: number, accuracy: number}>}
 */
export async function getCurrentPosition() {
  if (Capacitor.isNativePlatform()) {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 60000,
      });
      return position.coords;
    } catch (err) {
      console.warn('High accuracy failed, falling back to approximate', err);
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 120000,
      });
      return position.coords;
    }
  } else {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by your browser');
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos.coords),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 }
      );
    });
  }
}